import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Puzzle, PuzzleConstants} from './puzzle.js';
import {BoardObject, BounceUpData} from './board-object.js';
import {FailStateNames} from '../enums/FailStateNames.js';
import {DropMode, PuzzleCamera} from './puzzle-camera.js';
import {quat, vec3} from 'gl-matrix';
import {Mathfx} from '../utils/mathFx.js';
import {AnimationController} from '@wonderlandengine/tweenjs';
import {RNG} from '../utils/rng.js';

// Animations
//@ts-ignore
import IdleAnimation from './animations/Mal@idle.json' assert {type: 'json'};
//@ts-ignore
import BlinkAnimation from './animations/Mal@blink.json' assert {type: 'json'};
//@ts-ignore
import LoadAnimation from './animations/Mal@load.json' assert {type: 'json'};
//@ts-ignore
import DropAnimation from './animations/Mal@drop.json' assert {type: 'json'};
//@ts-ignore
import VictoryAAnimation from './animations/Mal@victory_a.json' assert {type: 'json'};
//@ts-ignore
import VictoryBAnimation from './animations/Mal@victory_b.json' assert {type: 'json'};
//@ts-ignore
import VictoryCAnimation from './animations/Mal@victory_c.json' assert {type: 'json'};
//@ts-ignore
import WorryBrowsAnimation from './animations/Mal@worry_brows.json' assert {type: 'json'};

import {WLUtils} from '../utils/WLUtils.js';
import {GameFlowController} from './game-flow-controller.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {Sounds} from '../enums/Sounds.js';
import {AudioDirector} from './audio-director.js';
import {CameraShakeParameters} from '../classes/CameraShakeParameters.js';
import {VEC3_ONE, VEC3_X, VEC3_ZERO} from '../utils/Constants.js';
import {Easing, lerp, lerpVec3} from '../utils/lerp.js';

export enum MalState {
    Idle,
    DropStart,
    DropEnd,
    Loading,
    Dying,
}

enum FruitAnimationState {
    start,
    loop,
    end,
}

const tempVec = vec3.create();
const tempQuat = quat.create();

export class MalAnimation extends Component {
    static TypeName = 'mal-animation';

    @property.vector3(0, 0, 0)
    pieceOffset = [0, 0, 0];

    @property.float(1)
    minimumDropDelay = 1;

    @property.float(0.3)
    pieceDropDelay = 0.3;

    @property.float(0.2)
    pieceAppearDelay = 0.2;

    @property.float(1.25)
    launchHeight = 1.25;

    @property.float(0.1)
    launchUpTime = 0.1;

    @property.float(5.0)
    minimumBlinkDelay = 5.0;

    @property.float(10.0)
    maximumBlinkDelay = 10.0;

    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    launchSFX: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    duckDownSFX: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    sparkleSFX: Sounds = Sounds.none;

    @property.float(0.64)
    absorbDeathMalReactionSeconds = 0.64;
    @property.float(0.3)
    absorbDeathReplaceMalAfterSeconds = 0.3;
    @property.float(0.5)
    absorbDeathBounceUpAfterSeconds = 0.5;
    @property.float(6)
    absorbDeathMaxBlobs = 6;
    @property.float(1.6)
    absorbDeathMinBlobScale = 1.6;
    @property.float(1.75)
    absorbDeathMaxBlobScale = 1.75;
    @property.float(1.3)
    absorbDeathScaleAdjustment = 1.3;
    @property.float(-0.375)
    absorbDeathHeightAdjustment = -0.375;

    @property.float(-0.2)
    flyingDeathTorque = -0.2;
    @property.float(1.8)
    flyingDeathFlightTimeSeconds = 1.8;
    @property.float(0.6)
    flyingDeathPauseForSparkleSeconds = 0.6;
    @property.float(2)
    flyingDeathFinishTimeSeconds = 2;
    @property.float(10)
    flyingDeathBackwardOffset = 10;
    @property.float(-4.5)
    flyingDeathSideOffset = -4.5;
    @property.float(-3)
    flyingDeathVerticalOffset = -3;
    @property.float(5)
    flyingDeathArcHeight = 5;
    @property.object({required: true})
    flyingDeathSparkle!: Object3D;

    @property.float(3)
    reachedMaxLevelFinishSeconds = 3;

    @property.object({required: true})
    loadedMal!: Object3D;

    private _state: MalState = MalState.Idle;
    private _puzzle: Puzzle;
    private _nextFruit: Object3D;
    private _dropPending: boolean;

    private _causeOfDeath = FailStateNames.None;

    private _time = 0;
    private _timeSinceDrop: number;
    private _lastDropTime: number = 0;
    private _timerStart: number;
    private _currentBlinkDelay: number = 0;
    private _fruitLoaded: boolean;
    private _animatingFruit: boolean;
    private _fruitAnimationState: FruitAnimationState;
    private _currentAnimatingFruit: Object3D;
    private _currentAnimatingFruit_offset: number;
    private _currentAnimatingFruit_startTime: number;
    private _currentAnimatingFruit_initalY: number;
    private _currentAnimatingFruit_elapsed: number;
    private _animator: AnimationController;

    private _coroutineManager = new CoroutineManager();

    private _cameraShakeParameters = new CameraShakeParameters(2, 2, 0.067, 3);

    init() {
        this._animatingFruit = false;
        this._fruitLoaded = false;
        this._dropPending = false;
        this._nextFruit = null;
    }

    start() {
        this._puzzle = Puzzle.instance;

        this._animator =
            this.object.getComponent(AnimationController) ??
            this.object.addComponent(AnimationController);

        this._animator.addClip(BlinkAnimation, 'blink');
        this._animator.addClip(IdleAnimation, 'idle');
        this._animator.addClip(LoadAnimation, 'load');
        this._animator.addClip(DropAnimation, 'drop');
        this._animator.addClip(WorryBrowsAnimation, 'worry');
        this._animator.addClip(VictoryAAnimation, 'victory1');
        this._animator.addClip(VictoryBAnimation, 'victory2');
        this._animator.addClip(VictoryCAnimation, 'victory3');

        this._animator.setLayer('blink', 2);
        this._animator.setLayer('worry', 1);

        this._animator.play('idle');
    }

    update(dt: number) {
        let elapsed: number;
        this._time += dt;

        this._coroutineManager.update(dt);

        if (this._animatingFruit) {
            this._animateFruit(dt);
        }

        switch (this._state) {
            case MalState.DropStart:
                elapsed = this._time - this._timerStart;
                if (elapsed > this.pieceDropDelay) {
                    this._launchCurrentFruit();
                    this._state = MalState.DropEnd;

                    AudioDirector.instance.playOneShot(this.launchSFX, 0.5, true);
                }
                break;
            case MalState.DropEnd:
                elapsed = this._time - this._timerStart;
                if (elapsed > this._animator.getLength('drop')) {
                    this._state = MalState.Idle;
                    this.load();
                }
                break;
            case MalState.Loading:
                elapsed = this._time - this._timerStart;
                if (elapsed > this.pieceAppearDelay) {
                    //Logger.Assert(nextFruit == null, "Fruit drop race condition!");
                    this._nextFruit = this._puzzle.spawner.queueTop();
                    WLUtils.setActive(this._nextFruit, true);
                    this._puzzle.spawner.popNext();
                    this._fruitLoaded = true;
                    this._state = MalState.Idle;

                    AudioDirector.instance.playOneShot(this.duckDownSFX, 0.5, true);
                }
                break;
            case MalState.Idle:
                this._timeSinceDrop = this._time - this._lastDropTime;
                if (this._causeOfDeath != FailStateNames.None) {
                    this._startDeath();
                } else if (
                    this._dropPending &&
                    this._fruitLoaded &&
                    (this._timeSinceDrop > this.minimumDropDelay ||
                        this._puzzle.mainCamera.currentDropMode == DropMode.Immediate)
                ) {
                    this._startDrop();
                }
                break;
            case MalState.Dying:
                break;
        }

        if (this._time > this._currentBlinkDelay && this._state != MalState.Dying) {
            this._animator.play('blink');
            this._currentBlinkDelay =
                this._time + RNG.getUniform(this.minimumBlinkDelay, this.maximumBlinkDelay);
        }
    }

    /**
     * Originally this was a coroutine in Unity, but we can't do that here.
     * Hence the odd construction.
     */
    private _animateFruit(dt: number = 0) {
        switch (this._fruitAnimationState) {
            case FruitAnimationState.start:
                this._fruitAnimationState = FruitAnimationState.loop;

                // animate the fruit up
                this._currentAnimatingFruit_offset = 0.0;
                this._currentAnimatingFruit_startTime = this._time;
                this._currentAnimatingFruit.getPositionWorld(tempVec);
                this._currentAnimatingFruit_initalY = tempVec[1];
                this._currentAnimatingFruit_elapsed = 0.0;

                this._currentAnimatingFruit.parent = null;
                this._currentAnimatingFruit.resetScaling();

                break;
            case FruitAnimationState.loop:
                this._currentAnimatingFruit_elapsed =
                    this._time - this._currentAnimatingFruit_startTime;
                const percentComplete =
                    this._currentAnimatingFruit_elapsed / this.launchUpTime;

                const offset = Mathfx.sinerp(0.0, this.launchHeight, percentComplete);

                this._currentAnimatingFruit.getPositionWorld(tempVec);
                tempVec[1] = this._currentAnimatingFruit_initalY + offset;
                this._currentAnimatingFruit.setPositionWorld(tempVec);

                if (this._currentAnimatingFruit_elapsed > this.launchUpTime) {
                    this._fruitAnimationState = FruitAnimationState.end;
                }
                break;
            case FruitAnimationState.end:
                const boardObject = this._currentAnimatingFruit.getComponent(BoardObject);
                const laneIndex = this._puzzle.mainCamera.findClosestLane();
                boardObject.drop(this._puzzle, -1, laneIndex);

                this._animatingFruit = false;
                break;
        }
    }

    private _launchCurrentFruit() {
        if (this._nextFruit) {
            this._fruitLoaded = false;

            this._animatingFruit = true;
            this._fruitAnimationState = FruitAnimationState.start;
            this._currentAnimatingFruit = this._nextFruit;
            this._animateFruit(); // trigger first sequence.

            this._nextFruit = null;
            this._lastDropTime = this._time;
        }
    }
    private _startDrop() {
        this._animator.play('drop');
        this._animator.playQueued('idle');

        this._state = MalState.DropStart;
        this._timerStart = this._time;
        this._dropPending = false;
    }

    private _startDeath() {
        this._state = MalState.Dying;
        if (this._fruitLoaded && this._nextFruit) {
            const boardComponent = this._nextFruit.getComponent(BoardObject);
            boardComponent.pop(0, 0, false);
            this._nextFruit = null;
        }

        switch (this._causeOfDeath) {
            case FailStateNames.BlobsOverflow:
                //   StartCoroutine("DoAbsorbedDeath");
                this._coroutineManager.addCoroutine(new Coroutine(this._doAbsorbedDeath()));
                break;

            case FailStateNames.FruitOverflow:
                //   StartCoroutine("DoFlyingDeath");
                this._coroutineManager.addCoroutine(new Coroutine(this._doFlyingDeath()));
                break;

            case FailStateNames.TimeUp:
                //   StartCoroutine("DoTimeUpDeath");
                break;

            case FailStateNames.CountdownExpired:
                //   StartCoroutine("DoCursedDeath");
                break;

            case FailStateNames.MaxLevelReached:
                this._coroutineManager.addCoroutine(
                    new Coroutine(this._doReachedMaxLevel())
                );
                break;

            case FailStateNames.TutorialWon:
                //   StartCoroutine("DoBeatTutorial");
                break;
        }
    }

    die(cause: FailStateNames) {
        this._causeOfDeath = cause;
    }

    drop() {
        this._dropPending = true;
    }

    resetMal() {
        this._state = MalState.Idle;
        this.object.parent.setScalingWorld(VEC3_ONE);
        this._animator.play('idle');
        this._timerStart = 0.0;
        this._animatingFruit = false;
        this._fruitLoaded = false;
        this._dropPending = false;
        this._causeOfDeath = FailStateNames.None;

        if (this._nextFruit) {
            this._nextFruit.destroy();
            this._nextFruit = null;
        }
        this._lastDropTime = 0;
        //const facial = this.object.getComponent(MalFacialAnimation);
        // if (facial)
        // {
        //     facial.setVictorious(false);
        // }
    }

    load() {
        if (this._state == MalState.Idle || this._state == MalState.Loading) {
            const queueNext = this._puzzle.spawner.queueTop();
            if (queueNext && queueNext != this._nextFruit) {
                if (this._nextFruit && this._nextFruit != queueNext) {
                    // we're going to return the currently loaded fruit to the stack.
                    this._puzzle.spawner.returnToQueue(this._nextFruit);
                    this._nextFruit = null;
                }

                // if we're already in the loading state that means that you can't see the
                // fruit yet, so we can just swap it out and continue.
                if (this._state == MalState.Idle) {
                    this._animator.stop('load');
                    this._animator.play('load');
                    this._animator.playQueued('idle');
                    this._timerStart = this._time;
                }
                this._state = MalState.Loading;

                queueNext.parent = this.object;
                queueNext.setPositionLocal(this.pieceOffset);

                const boardComponent = queueNext.getComponent(BoardObject);
                boardComponent.pause();
            }
        }
    }

    private *_doAbsorbedDeath() {
        this._animator.stop('blink');

        const columnCount = this._puzzle.lanes.length;
        const topRow = PuzzleConstants.rowCount - 1;
        let blob: BoardObject = null; // The blob that will be jumping on mal
        let maxDelay = 0.0;
        let hopObjectList: BoardObject[] = [];
        let hopDelayList: number[] = [];
        let topBlobCount = 0;
        for (let x = 0; x < columnCount; x++) {
            const obj = this._puzzle.getObject(topRow, x);
            if (obj) {
                if (!obj.isFruit) {
                    if (blob == null) {
                        blob = obj;
                    } else {
                        const delay = RNG.getUniform(0.05 * topBlobCount, 1);
                        if (delay > maxDelay) {
                            maxDelay = delay;
                        }
                        hopObjectList.push(obj);
                        hopDelayList.push(delay);
                    }
                    topBlobCount++;
                } else {
                    obj.pop(0, 0, false);
                }
            }
        }
        for (let i = 0; i < hopObjectList.length; i++) {
            hopObjectList[i].hopOnMal(
                null,
                hopDelayList[i],
                0.0,
                quat.create(),
                1.0,
                maxDelay + 0.2 + this.absorbDeathBounceUpAfterSeconds
            );
        }
        const rotation = this.object.getRotationWorld();
        const blobScale =
            Math.min(hopObjectList.length / this.absorbDeathMaxBlobs, 1.0) *
                (this.absorbDeathMaxBlobScale - this.absorbDeathMinBlobScale) +
            this.absorbDeathMinBlobScale;

        const copyOfMal = this.loadedMal.clone();
        WLUtils.setActive(copyOfMal, false);
        const bounce: BounceUpData = {
            copyOfMal: copyOfMal,
            scaleAdjustment: this.absorbDeathScaleAdjustment,
            heightAdjustment: this.absorbDeathHeightAdjustment,
        };
        blob.hopOnMal(
            bounce,
            maxDelay, // + 0.2,
            0, //this.absorbDeathBounceUpAfterSeconds,
            rotation,
            blobScale,
            0.0
        );

        yield* waitForSeconds(this.absorbDeathMalReactionSeconds);
        this._animator.play('load');
        yield* waitForSeconds(this.absorbDeathReplaceMalAfterSeconds);
        this.object.parent.setScalingWorld(VEC3_ZERO); // hide mal
        yield* waitForSeconds(maxDelay + 0.2 + 3);
        GameFlowController.instance.gameOver();
    }

    private *_doFlyingDeath() {
        const startPosition = this.object.getPositionWorld();
        const currentPosition = vec3.create();
        const targetPosition = vec3.create();

        const malForward = vec3.create();
        this.object.getForwardWorld(malForward);
        const malRight = vec3.create();
        this.object.getRightWorld(malRight);

        // Move backward along forward vector
        vec3.scaleAndAdd(
            targetPosition,
            startPosition,
            malForward,
            this.flyingDeathBackwardOffset
        );
        // Move sideways along right vector
        vec3.scaleAndAdd(
            targetPosition,
            targetPosition,
            malRight,
            this.flyingDeathSideOffset
        );
        // Move vertically
        targetPosition[1] += this.flyingDeathVerticalOffset;

        // Clone and position flying Mal
        const flyingMal = this.loadedMal.clone();
        flyingMal.setPositionWorld(startPosition);
        flyingMal.setRotationWorld(this.object.getRotationWorld());
        flyingMal.translateLocal([0, -1, 0]);

        // Hide original Mal
        this.object.parent.setScalingWorld(VEC3_ZERO);

        const startTime = this._time;

        // pop any fruit at the top of the stack
        const columnCount = this._puzzle.lanes.length;
        const topRow = PuzzleConstants.rowCount - 1;
        for (let x = 0; x < columnCount; x++) {
            const obj = this._puzzle.getObject(topRow, x);
            if (obj && obj.isFruit) {
                obj.pop(0, 0, false);
            }
        }

        // Shake camera
        this._puzzle.mainCamera.shakeCamera(this._cameraShakeParameters);

        // Animate flying Mal
        while (this._time < startTime + this.flyingDeathFlightTimeSeconds) {
            const elapsed = this._time - startTime;
            const t = elapsed / this.flyingDeathFlightTimeSeconds;
            lerpVec3(currentPosition, startPosition, targetPosition, t, Easing.OutQuad);
            const scalefactor = lerp(1, 0, t, Easing.InQuad);

            const heightOffset = Math.sin(Math.PI * t) * this.flyingDeathArcHeight;
            currentPosition[1] =
                startPosition[1] +
                (targetPosition[1] - startPosition[1]) * t +
                heightOffset;

            flyingMal.setScalingWorld([scalefactor, scalefactor, scalefactor]);
            flyingMal.rotateAxisAngleRadLocal(VEC3_X, this.flyingDeathTorque * elapsed);
            flyingMal.setPositionWorld(currentPosition);

            yield null;
        }

        // Create sparkle effect
        const sparkle = this.flyingDeathSparkle.clone();
        WLUtils.setActive(sparkle, false);

        sparkle.setPositionWorld(flyingMal.getPositionWorld());

        flyingMal.destroy();

        yield* waitForSeconds(this.flyingDeathPauseForSparkleSeconds);
        WLUtils.setActive(sparkle, true);

        AudioDirector.instance.playOneShot(Sounds.sparkle, true);

        yield* waitForSeconds(this.flyingDeathFinishTimeSeconds);

        sparkle.destroy();
        GameFlowController.instance.gameOver();
    }

    private *_doReachedMaxLevel() {
        this._setWorried(false);
        this._animator.play(RNG.getItem(['victory1', 'victory2', 'victory3']));

        //const facial = this.object.getComponent(MalFacialAnimation);
        // if (facial)
        // {
        // 	facial.setVictorious(true);
        // }
        yield* waitForSeconds(
            this.reachedMaxLevelFinishSeconds + this._puzzle.delayForVictoryExplosion
        );
        const gameFlow = GameFlowController.instance;
        gameFlow.gameOver();
        while (gameFlow.transition.transitionInProgress) {
            yield null;
        }
        // if (facial)
        // {
        // 	facial.setVictorious(false);
        // }
    }

    private _setWorried(isWorried: boolean) {
        //const facial = this.object.getComponent(MalFacialAnimation);
        // if (facial && facial.IsFailure)
        // {
        // 	return;
        // }
        if (isWorried) {
            // && animator.IsPlaying("worry"))
            this._animator.play('worry');
        } // if (!isWorried && animator.IsPlaying("worry"))
        else {
            this._animator.stop('worry');
        }
    }
}
